import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useUserData } from './UserDataContext';
import { Eye, Minimize2, Maximize2, X } from 'lucide-react';
import { WatcherActionPanel } from './WatcherActionPanel';
import { SafeStorage } from '../utils/safeStorage';
import { getDbSafe } from '../services/firebaseConfig';
import { collection, query, where, onSnapshot } from 'firebase/firestore';

interface Position {
    x: number;
    y: number;
}

// v2: reset once to the new top/collapsed default (old key held the previous
// bottom-right auto-saved position). Future drags persist under this key.
const STORAGE_KEY = 'watcher_remote_position_v2';

// Safe-area inset at the top (notch / status bar) in px, probed from CSS env().
function safeAreaTop(): number {
    try {
        const probe = document.createElement('div');
        probe.style.cssText = 'position:fixed;top:0;height:env(safe-area-inset-top,0px);visibility:hidden;pointer-events:none;';
        document.body.appendChild(probe);
        const h = probe.getBoundingClientRect().height;
        document.body.removeChild(probe);
        return Number.isFinite(h) ? h : 0;
    } catch {
        return 0;
    }
}

/**
 * Collapsed height of the Legion remote's pill, plus a gap.
 *
 * Both remotes are rendered for a Legion mod and both used to spawn at exactly
 * { x: 12, y: safeAreaTop() + 12 }. AppRouter mounts Watcher second, so it
 * painted on top and hid the Legion remote completely — including its
 * flagged-accounts badge, which is the count of new accounts awaiting review.
 * The stacking this constant provides was always the stated intent; the offset
 * implementing it simply was not there.
 */
const LEGION_REMOTE_STACK_OFFSET = 58;

// Initial spawn: near the TOP-LEFT of the screen, below the safe-area inset so
// it clears the notch/status bar, and below the Legion remote when that one is
// on screen too. Only used on first appearance — the remote stays freely
// draggable for the rest of the session.
function getDefaultPosition(belowLegionRemote = false): Position {
    return {
        x: 12,
        y: safeAreaTop() + 12 + (belowLegionRemote ? LEGION_REMOTE_STACK_OFFSET : 0),
    };
}

export function WatcherRemote() {
    const { userProfile } = useUserData();
    const [position, setPosition] = useState<Position>(() => getDefaultPosition(false));

    // Drop below the Legion remote once we know this mod is Legion and therefore
    // has both on screen.
    //
    // Applied in an effect rather than in the useState initialiser because rank
    // arrives with the profile, which is not resolved on the first render. It
    // runs at most once, and only before the remote is interactive: the render
    // gate below returns null until rank is known, so there is no window in
    // which this could yank a remote out from under a drag.
    const isLegion = String(userProfile?.rank || '').toLowerCase() === 'legion';
    const stackedRef = useRef(false);
    useEffect(() => {
        if (!isLegion || stackedRef.current) return;
        stackedRef.current = true;
        setPosition(getDefaultPosition(true));
    }, [isLegion]);
    // Default to the compact collapsed pill (icon + name + X + expand); the user
    // taps the maximize icon to open the full remote.
    const [isMinimized, setIsMinimized] = useState(true);
    const [isExpanded, setIsExpanded] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState<Position>({ x: 0, y: 0 });
    const [pendingReports, setPendingReports] = useState<number>(0);
    const remoteRef = useRef<HTMLDivElement>(null);
    const unsubRef = useRef<(() => void) | null>(null);

    // Deliberately NOT restoring a saved position at boot.
    //
    // The remote is meant to start in a known place every launch — top
    // left — so it is never off-screen after a rotation or a smaller
    // window, and is never hunted for. It stays freely draggable for the
    // rest of the session; the move simply does not outlive the launch.

    // Live pending reports count
    useEffect(() => {
        const rank = String(userProfile?.rank || '').toLowerCase();
        if (!userProfile?.id || (rank !== 'watcher' && rank !== 'legion')) return;
        let active = true;
        (async () => {
            try {
                const db = await getDbSafe();
                const q = query(collection(db, 'reports'), where('status', '==', 'pending'));
                const unsub = onSnapshot(q, snap => {
                    if (active) setPendingReports(snap.size);
                }, () => {});
                unsubRef.current = unsub;
            } catch { /* silent */ }
        })();
        return () => {
            active = false;
            unsubRef.current?.();
        };
    }, [userProfile?.id, userProfile?.rank]);

    // Save position when it changes
    useEffect(() => {
        SafeStorage.setItem(STORAGE_KEY, JSON.stringify(position));
    }, [position]);

    const [isHidden, setIsHidden] = useState(false);

    // Dragging used to be blocked whenever the remote was expanded, so the only
    // way to move it was to collapse it first. It now drags in either state; the
    // guard is on WHAT was pressed rather than on the remote's size, so a press
    // that lands on a button, link or field still does that thing instead of
    // starting a drag.
    const isInteractive = (target: EventTarget | null): boolean =>
        !!(target as HTMLElement | null)?.closest?.('button, a, input, select, textarea, [role="button"]');

    const handleMouseDown = (e: React.MouseEvent) => {
        if (isInteractive(e.target)) return;
        setIsDragging(true);
        setDragOffset({
            x: e.clientX - position.x,
            y: e.clientY - position.y,
        });
    };

    const handleTouchStart = (e: React.TouchEvent) => {
        if (isInteractive(e.target)) return;
        setIsDragging(true);
        const touch = e.touches[0];
        setDragOffset({
            x: touch.clientX - position.x,
            y: touch.clientY - position.y,
        });
    };

    const handleMouseMove = (e: MouseEvent) => {
        if (!isDragging) return;
        e.preventDefault();
        const newX = e.clientX - dragOffset.x;
        const newY = e.clientY - dragOffset.y;
        updatePosition(newX, newY);
    };

    const handleTouchMove = (e: TouchEvent) => {
        if (!isDragging) return;
        const touch = e.touches[0];
        const newX = touch.clientX - dragOffset.x;
        const newY = touch.clientY - dragOffset.y;
        updatePosition(newX, newY);
    };

    const updatePosition = (x: number, y: number) => {
        const maxX = window.innerWidth - (remoteRef.current?.offsetWidth || 280);
        const maxY = window.innerHeight - (remoteRef.current?.offsetHeight || 100);
        setPosition({
            x: Math.max(0, Math.min(x, maxX)),
            y: Math.max(0, Math.min(y, maxY)),
        });
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    useEffect(() => {
        if (isDragging) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
            window.addEventListener('touchmove', handleTouchMove, { passive: false });
            window.addEventListener('touchend', handleMouseUp);
            return () => {
                window.removeEventListener('mousemove', handleMouseMove);
                window.removeEventListener('mouseup', handleMouseUp);
                window.removeEventListener('touchmove', handleTouchMove);
                window.removeEventListener('touchend', handleMouseUp);
            };
        }
    }, [isDragging, dragOffset]);

    const toggleMinimize = () => {
        setIsMinimized(!isMinimized);
        setIsExpanded(false);
    };

    const toggleExpand = () => {
        setIsExpanded(!isExpanded);
        setIsMinimized(false);
    };

    // Only render for Watcher/Legion mods who are actually logged in
    const modRank = String(userProfile?.rank || '').toLowerCase();
    if (!userProfile?.id || (modRank !== 'watcher' && modRank !== 'legion') || isHidden || typeof document === 'undefined') {
        return null;
    }

    return createPortal(
        <>
            <div
                ref={remoteRef}
                style={{
                    position: 'fixed',
                    left: `${position.x}px`,
                    top: `${position.y}px`,
                    zIndex: 9999999, // Extremely High Z-Index to stay on top
                    cursor: isDragging ? 'grabbing' : 'grab',
                    touchAction: 'none', // Prevent scrolling while dragging
                    pointerEvents: 'none' // Allow clicks to pass through container
                }}
                onMouseDown={handleMouseDown}
                onTouchStart={handleTouchStart}
            >
                <div
                    className="rounded-lg shadow-2xl border backdrop-blur-sm"
                    style={{
                        backgroundColor: 'rgba(10, 10, 10, 0.95)',
                        borderColor: '#DC2626',
                        boxShadow: '0 0 20px rgba(220, 38, 38, 0.3)',
                        pointerEvents: 'auto' // Re-enable pointer events for the card itself
                    }}
                >
                    {/* Header */}
                    <div className="flex items-center justify-between p-3 border-b" style={{ borderColor: 'rgba(220, 38, 38, 0.3)' }}>
                        <div className="flex items-center gap-2">
                            <Eye
                                className="w-5 h-5"
                                style={{ color: '#DC2626' }}
                            />
                            <span
                                className="font-bold text-sm tracking-widest uppercase"
                                style={{ color: '#DC2626' }}
                            >
                                {isMinimized ? 'Watcher' : 'Watcher Remote'}
                            </span>
                            {/* Live report count badge */}
                            {pendingReports > 0 && (
                                <span
                                    className="inline-flex items-center justify-center rounded-full text-[10px] font-black min-w-[18px] h-[18px] px-1"
                                    style={{
                                        background: '#DC2626',
                                        color: '#fff',
                                        boxShadow: '0 0 6px rgba(220,38,38,0.7)',
                                        animation: 'pulse 1.5s ease-in-out infinite',
                                    }}
                                >
                                    {pendingReports}
                                </span>
                            )}
                        </div>
                        <div className="flex items-center gap-1">
                            {/* Hide Button */}
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (window.confirm('Hide Watcher Remote? You will need to reload the app to see it again.')) {
                                        setIsHidden(true);
                                    }
                                }}
                                className="p-1 rounded hover:bg-white/10 transition-colors"
                                style={{ color: '#DC2626' }}
                                title="Hide completely (Reload to restore)"
                            >
                                <X className="w-4 h-4" />
                            </button>

                            {/* Minimize Button */}
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggleMinimize();
                                }}
                                className="p-1 rounded hover:bg-white/10 transition-colors"
                                style={{ color: '#DC2626' }}
                            >
                                {isMinimized ? <Maximize2 className="w-4 h-4" /> : <Minimize2 className="w-4 h-4" />}
                            </button>
                        </div>
                    </div>

                    {/* Body - Only show when not minimized */}
                    {!isMinimized && (
                        <div className="p-3 space-y-2">
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggleExpand();
                                }}
                                className="w-full px-4 py-2 rounded-lg font-black text-sm transition-all hover:scale-105 uppercase tracking-wider shadow-lg"
                                style={{
                                    backgroundColor: '#DC2626',
                                    color: '#ffffff',
                                    boxShadow: '0 0 16px rgba(220, 38, 38, 0.4)'
                                }}
                            >
                                {isExpanded ? 'Close System' : 'Access System'}
                            </button>

                            <div className="text-[10px] text-center opacity-60 uppercase tracking-widest" style={{ color: '#DC2626' }}>
                                Drag to move
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Action Panel - Rendered separately */}
            {isExpanded && (
                <WatcherActionPanel
                    onClose={() => setIsExpanded(false)}
                />
            )}
        </>,
        document.body
    );
}
